// Booking flow state machine.
//
// One useReducer drives the entire widget. Each step in the flow
// produces an action; the reducer advances the state and emits a
// step-changed event for analytics. State is intentionally flat —
// React 18 batches updates automatically; we don't need RTK.

import type {
  Location,
  FittingType,
  BookingResponse,
  PromoDiscount,
  LookedUpAppointment,
} from '../types';
import { DEFAULT_PROMO_CODE, DEFAULT_PROMO_TERMS, MOCK_VALIDATED_PROMO_TERMS, defaultPromoTermsForCountry } from '../data/promoTerms';
import { countryConfigOrDefault } from '../config/countries';
import type { ResolvedFittingPrice } from '../data/promoPricing';

// MergedSlot is the shape returned by the api-client's getAvailability —
// re-declared here to avoid a circular import.
export interface SelectedSlot {
  start: string;
  end: string;
  fitterId: string;
  fitterName: string;
}

export interface BookingState {
  // Step 1: lead capture (always-visible top of form)
  fullName: string;
  email: string;
  // Phone moved into Step 1 for the inline landing page (was previously
  // collected in the post-booking GetSetUp form).
  phone: string;
  promoOptIn: boolean;
  /**
   * The market's required consent (UK: the privacy policy) has been ticked.
   *
   * Gate only. It is never put in an API payload — every payload here names
   * its fields explicitly, so this stays local by construction, and CC asked
   * for it not to be recorded.
   */
  privacyAccepted: boolean;
  /**
   * Dial code the customer typed, where the market allows it to be edited.
   * Null means "use the market's own", which is the case everywhere else and
   * until they change it here.
   */
  dialCodeOverride: string | null;

  // Country scope — set from data-cc-countrycode on the mount element at
  // engage. Passed to the middleware /locations query to filter the store
  // list by market ("us" | "uk" | "ca" | "au"). Null = US default (server
  // preserves pre-country behavior).
  country: string | null;

  // Step 2: location search + selection
  locationQuery: string;
  nearbyLocations: Location[];
  selectedLocation: Location | null;
  /**
   * Store-specific canonical→wire fitting-name map. Populated by widget.tsx
   * after SELECT_LOCATION resolves against the middleware /fittings endpoint.
   * `null` means either no store is selected, the fetch hasn't completed,
   * or the fetch failed — in every case callers fall back to the canonical
   * US name (preserving pre-international behavior). International stores
   * REQUIRE the map because SF's Product2 records are country-suffixed.
   */
  fittingNameMap: Record<string, string> | null;
  /**
   * The studio's list price per wire fitting name, from the same /fittings
   * answer as fittingNameMap. What /promos/validate is sent as `price`, so
   * the applied line's "(was $X)" is the fee without a purchase rather than
   * the label's with-purchase figure (Dan, 2026-10-13).
   */
  fittingListPrices: Record<string, number> | null;
  /**
   * Promotional price per fitting wire name, resolved from Salesforce rather
   * than authored. Null until a store is chosen, since promotions are
   * store-scoped and /promos/validate needs a location.
   *
   * @see data/promoPricing.ts
   */
  promoPricing: Record<string, ResolvedFittingPrice> | null;

  // Step 3: fitting type
  selectedFittingType: FittingType | null;

  // Step 4: date
  selectedDate: string | null; // YYYY-MM-DD

  // Step 5: fitter preference (user-selected — null = "Any Fitter" intent)
  selectedFitterId: string | null;

  // Step 6: time slot
  // selectedTime is the ISO start; selectedSlot keeps the full slot so we
  // know which fitter to book against when the user chose "Any Fitter"
  selectedTime: string | null;
  selectedSlot: SelectedSlot | null;
  /**
   * A slot start time promised upstream (the seed card's NEXT AVAILABLE) to
   * auto-select once TimeSlots loads the real list. Cleared on any change
   * that would make the promise stale — location or fitting — and once
   * consumed. The auto-pick only fires when the loaded list actually
   * contains the time, so a promise that expired between render and click
   * quietly degrades to the normal picker.
   */
  pendingSlotStart: string | null;

  // Step 7: promo + confirm
  /**
   * Auto-applied "master" promo — the country default at engage, replaced
   * by the SF-active promo (activePromoCode from /fittings) on store
   * select. Sent on Schedule as the base code. NEVER shown in the promo
   * input — that field is reserved for the user's own optional code
   * (typically a referral). Building the Schedule payload concatenates
   * both as `${master};${userValidatedCode}` when the user has validated
   * a distinct additional code.
   */
  masterPromoCode: string;
  /** User-entered promo (visible in the input). Empty = no user code. */
  promoCode: string;
  /** In-flight POST /waitlist request from the no-availability empty state. */
  waitlistSubmitting: boolean;
  waitlistError: string | null;
  /** In-flight POST /promos/validate request. */
  promoValidating: boolean;
  /** Result of the most recent successful validation call (success or invalid). */
  promoValidation: { valid: boolean; discount?: PromoDiscount; message?: string } | null;
  /** The exact promoCode value that produced `promoValidation`. Used to know
   *  when to invalidate the result (user edited the code after validating). */
  promoValidatedCode: string | null;
  /** Network/transport error from a validate call — distinct from "invalid". */
  promoValidationError: string | null;
  /**
   * Terms-and-conditions text for the currently-applied promo. Defaults to
   * the FITTINGUS terms; replaced with the validated promo's terms when a
   * non-FITTINGUS code applies cleanly. Left untouched on validation
   * errors or invalid codes so the default terms keep showing.
   */
  promoTerms: string;
  /**
   * SF-sourced FITTINGUS terms, fetched silently on widget mount via
   * /promos/validate so the "See Terms and Conditions" modal on the
   * confirm screen and the "View details" modal on the panel always
   * surface live promo copy instead of the bundled stub. Falls back to
   * DEFAULT_PROMO_TERMS when the prefetch hasn't completed (or failed).
   */
  defaultPromoTermsLive: string | null;
  submitting: boolean;
  submitError: string | null;

  // Step 8: confirmation (response from middleware POST /bookings)
  confirmation: BookingResponse | null;

  /**
   * Flips true when the user clicks the disabled Book Fitting CTA with
   * unfilled required fields. Each field with no value renders with a red
   * stroke until the user fills it in (the SELECT_* actions auto-clear).
   */
  showRequiredErrors: boolean;

  // Post-booking
  /**
   * "How did you hear about us?" — asked on the confirm step, not after.
   *
   * Salesforce accepts this on Schedule and nowhere else: its
   * PostAppointmentUpdate takes only phone and SMS consent. So it has to be
   * captured before the booking exists in order to reach the record at all.
   */
  hearAbout: string | null;
  hearAboutAdditional: string;
  postBookingHandedness: 'left' | 'right' | null;
  postBookingBallFitting: boolean;
  postBookingPhone: string;
  postBookingSmsOptIn: boolean;
  /**
   * The customer's own postcode, asked on the confirmation screen and sent
   * with the post-appointment update. Distinct from whatever they typed into
   * studio search, which is where they were looking rather than where they
   * live.
   */
  postBookingPostalCode: string;
  postBookingSubmitting: boolean;
  postBookingError: string | null;
  postBookingComplete: boolean;

  // Reschedule flow (entered from the Step-1 "Reschedule" link).
  /**
   * True while the widget is moving an existing appointment rather than
   * creating one. Drives Step-1 validation (name+email only), the confirm
   * screen's "Reschedule Your Appointment" header (which must survive every
   * Change round-trip — it derives from this flag, never from navigation),
   * and the submit path. ALL reschedule fields reset via initialState on
   * RESET (widget close/restart).
   */
  rescheduleMode: boolean;
  /**
   * Multiple upcoming appointments matched the lookup — the user picks one
   * from RescheduleAppointmentPicker. Null when no lookup has run, exactly
   * one matched (auto-hydrated), or one has been chosen.
   */
  rescheduleCandidates: LookedUpAppointment[] | null;
  /** The appointment being moved. Its (WL-stripped) number keys the POST. */
  originalAppointment: LookedUpAppointment | null;
  /** In-flight GET /bookings/lookup. */
  rescheduleLookupSubmitting: boolean;
  /** Lookup failure copy — 404 and network errors, rendered via AlertMessage. */
  rescheduleLookupError: string | null;
}

export type BookingAction =
  | {
      type: 'SET_LEAD';
      payload: Partial<
        Pick<
          BookingState,
          'fullName' | 'email' | 'phone' | 'promoOptIn' | 'privacyAccepted' | 'dialCodeOverride'
        >
      >;
    }
  | { type: 'SET_LOCATION_QUERY'; payload: string }
  | { type: 'SET_NEARBY_LOCATIONS'; payload: Location[] }
  | { type: 'SELECT_LOCATION'; payload: Location | null }
  | { type: 'SELECT_FITTING_TYPE'; payload: FittingType | null }
  | { type: 'SELECT_DATE'; payload: string | null }
  | { type: 'SELECT_FITTER'; payload: string | null }
  | { type: 'SELECT_TIME'; payload: string | null }
  | { type: 'SELECT_SLOT'; payload: SelectedSlot }
  | { type: 'SET_PENDING_SLOT'; payload: string | null }
  | {
      type: 'SWITCH_TO_SLOT_AT_LOCATION';
      payload: {
        location: Location;
        fittingType: FittingType;
        date: string;
        slot: SelectedSlot;
      };
    }
  | { type: 'SET_COUNTRY'; payload: string | null }
  | { type: 'SET_FITTING_NAME_MAP'; payload: Record<string, string> | null }
  | { type: 'SET_FITTING_LIST_PRICES'; payload: Record<string, number> | null }
  | { type: 'SET_PROMO_PRICING'; payload: Record<string, ResolvedFittingPrice> | null }
  | {
      /**
       * Auto-apply a master promo without touching the user-visible input.
       * On engage: fires with the country-default initialPromoCode.
       * On SELECT_LOCATION: fires with the SF-active code from /fittings.
       * Also updates promoTerms so the "See Terms and Conditions" modal
       * reflects the applied master. Never touches state.promoCode.
       */
      type: 'SET_MASTER_PROMO';
      payload: { code: string; terms: string | null };
    }
  | { type: 'SET_PROMO_CODE'; payload: string }
  | { type: 'VALIDATE_PROMO_START' }
  | {
      type: 'VALIDATE_PROMO_SUCCESS';
      payload: {
        code: string;
        valid: boolean;
        discount?: PromoDiscount;
        message?: string;
        /** Terms text from the SF response. Empty/undefined leaves the
         *  current promoTerms in place — falls back to DEFAULT_PROMO_TERMS
         *  if none was previously set. */
        terms?: string;
      };
    }
  | { type: 'VALIDATE_PROMO_ERROR'; payload: string }
  | { type: 'SET_DEFAULT_PROMO_TERMS_LIVE'; payload: string }
  | { type: 'WAITLIST_SUBMIT_START' }
  | { type: 'WAITLIST_SUBMIT_SUCCESS'; payload: BookingResponse }
  | { type: 'WAITLIST_SUBMIT_ERROR'; payload: string }
  | { type: 'SUBMIT_START' }
  | { type: 'SUBMIT_SUCCESS'; payload: BookingResponse }
  | { type: 'SUBMIT_ERROR'; payload: string }
  | { type: 'CLEAR_SUBMIT_ERROR' }
  | { type: 'FLASH_REQUIRED_ERRORS' }
  | {
      type: 'SET_HEAR_ABOUT';
      payload: { hearAbout?: string | null; hearAboutAdditional?: string };
    }
  | { type: 'POST_BOOKING_FIELD'; payload: Partial<BookingState> }
  | { type: 'POST_BOOKING_SUBMIT_START' }
  | { type: 'POST_BOOKING_SUBMIT_SUCCESS' }
  | { type: 'POST_BOOKING_SUBMIT_ERROR'; payload: string }
  | { type: 'RESET_PRESERVE_LEAD' }
  | { type: 'RESET' }
  | { type: 'ENTER_RESCHEDULE_MODE' }
  | { type: 'EXIT_RESCHEDULE_MODE' }
  | { type: 'RESCHEDULE_LOOKUP_START' }
  | { type: 'RESCHEDULE_LOOKUP_SUCCESS'; payload: { candidates: LookedUpAppointment[] } }
  | { type: 'RESCHEDULE_LOOKUP_ERROR'; payload: { message: string } }
  | {
      /**
       * Atomic hydration of the whole booking selection from a looked-up
       * appointment — modeled on SWITCH_TO_SLOT_AT_LOCATION so the
       * SELECT_LOCATION cascade cannot wipe the hydrated values. Sets
       * fittingNameMap to null; widget.tsx's listStoreFittings effect
       * (keyed on selectedLocation, which this changes from null) re-fetches
       * the store's canonical→wire map.
       */
      type: 'HYDRATE_FROM_APPOINTMENT';
      payload: {
        appointment: LookedUpAppointment;
        location: Location;
        fittingType: FittingType | null;
        date: string | null;
        slot: SelectedSlot | null;
        phone: string;
      };
    };

export const initialState: BookingState = {
  fullName: '',
  email: '',
  phone: '',
  // Pre-checked by default per CC marketing — most users keep marketing opt-in
  // on. Users opt OUT by unchecking before submitting lead capture.
  promoOptIn: true,
  // Unticked, always. This is the one consent that has to be an active choice
  // — a pre-ticked required box is not a gate, it is a formality.
  privacyAccepted: false,
  dialCodeOverride: null,
  country: null,
  locationQuery: '',
  nearbyLocations: [],
  selectedLocation: null,
  fittingNameMap: null,
  fittingListPrices: null,
  promoPricing: null,
  selectedFittingType: null,
  selectedDate: null,
  selectedFitterId: null,
  selectedTime: null,
  selectedSlot: null,
  pendingSlotStart: null,
  // Master starts at FITTINGUS as a legacy safety net; overwritten by
  // country-default on engage and again by /fittings.activePromoCode
  // on store select. Never visible to the user.
  masterPromoCode: DEFAULT_PROMO_CODE,
  // Field starts blank — user's own optional code goes here.
  promoCode: '',
  waitlistSubmitting: false,
  waitlistError: null,
  promoValidating: false,
  promoValidation: null,
  promoValidatedCode: null,
  promoValidationError: null,
  promoTerms: DEFAULT_PROMO_TERMS,
  defaultPromoTermsLive: null,
  submitting: false,
  submitError: null,
  confirmation: null,
  hearAbout: null,
  hearAboutAdditional: '',
  postBookingHandedness: null,
  postBookingBallFitting: false,
  postBookingPhone: '',
  postBookingSmsOptIn: false,
  postBookingPostalCode: '',
  postBookingSubmitting: false,
  postBookingError: null,
  postBookingComplete: false,
  showRequiredErrors: false,
  rescheduleMode: false,
  rescheduleCandidates: null,
  originalAppointment: null,
  rescheduleLookupSubmitting: false,
  rescheduleLookupError: null,
};

export function reducer(state: BookingState, action: BookingAction): BookingState {
  switch (action.type) {
    case 'SET_LEAD':
      return { ...state, ...action.payload };

    case 'SET_LOCATION_QUERY':
      return { ...state, locationQuery: action.payload };

    case 'SET_NEARBY_LOCATIONS':
      return { ...state, nearbyLocations: action.payload };

    // The cascade rule for these "SELECT_*" actions is: only invalidate
    // things that genuinely depend on the field being changed. Time + slot
    // depend on every upstream field, so they always clear. Location change
    // clears fitting type, date, fitter, and slots — availability is
    // studio-specific. Date and fitting type are otherwise independent.

    case 'SELECT_LOCATION':
      return {
        ...state,
        selectedLocation: action.payload,
        // Wire-name map is store-specific — clear until widget.tsx's effect
        // repopulates it from /fittings. Downstream calls fall back to
        // canonical names during the fetch window (safe for US, wrong for
        // international — the UI blocks fitting selection until the map lands).
        fittingNameMap: null,
        fittingListPrices: null,
        promoPricing: null,
        // Fitting type, date, fitter, and slots are location-scoped — changing
        // studio clears them so the user re-enters in order.
        selectedFittingType: null,
        selectedDate: null,
        selectedFitterId: null,
        selectedTime: null,
        selectedSlot: null,
        pendingSlotStart: null,
      };

    case 'SELECT_FITTING_TYPE':
      return {
        ...state,
        selectedFittingType: action.payload,
        // Slots are computed against the fitting type's durationMin, so the
        // current slot is stale. Date and fitter stay.
        selectedTime: null,
        selectedSlot: null,
        // A promised time was promised for a specific fitting; changing the
        // fitting invalidates it rather than auto-picking a stale time.
        pendingSlotStart: null,
        showRequiredErrors: false,
      };

    case 'SET_PENDING_SLOT':
      return { ...state, pendingSlotStart: action.payload };

    case 'SELECT_DATE':
      return {
        ...state,
        selectedDate: action.payload,
        selectedTime: null,
        selectedSlot: null,
        showRequiredErrors: false,
      };

    case 'SELECT_FITTER':
      return {
        ...state,
        selectedFitterId: action.payload,
        selectedTime: null,
        selectedSlot: null,
      };

    case 'SELECT_TIME':
      return { ...state, selectedTime: action.payload, selectedSlot: null, showRequiredErrors: false };

    case 'SELECT_SLOT':
      return {
        ...state,
        selectedTime: action.payload.start,
        selectedSlot: action.payload,
        showRequiredErrors: false,
      };

    case 'SWITCH_TO_SLOT_AT_LOCATION':
      // Atomic move when the user picks a slot at a different nearby location
      // from the cross-location expansion. Goes around the per-field cases'
      // cascading resets — sets every field that defines a booking in one shot.
      return {
        ...state,
        selectedLocation: action.payload.location,
        selectedFittingType: action.payload.fittingType,
        selectedDate: action.payload.date,
        selectedFitterId: null,
        selectedTime: action.payload.slot.start,
        selectedSlot: action.payload.slot,
      };

    case 'SET_COUNTRY': {
      const cfg = countryConfigOrDefault(action.payload);
      return {
        ...state,
        country: action.payload,
        // Terms default follows the market. FITTINGUS is US copy and must not
        // stand in for another market's promotion; AU has its own. Only replace
        // the untouched US default — a store's activePromoTerms or a visitor's
        // applied code (both of which move promoTerms off DEFAULT_PROMO_TERMS)
        // stay put.
        promoTerms:
          state.promoTerms === DEFAULT_PROMO_TERMS
            ? state.defaultPromoTermsLive ?? defaultPromoTermsForCountry(action.payload)
            : state.promoTerms,
        // Required-consent default. AU pre-ticks its privacy box (client spec);
        // the UK leaves it an active choice (unticked). Only ever pre-tick —
        // never silently untick a box the visitor already accepted.
        privacyAccepted: cfg.requiredConsent?.defaultChecked ? true : state.privacyAccepted,
      };
    }

    case 'SET_FITTING_NAME_MAP':
      return { ...state, fittingNameMap: action.payload };

    case 'SET_FITTING_LIST_PRICES':
      return { ...state, fittingListPrices: action.payload };

    case 'SET_PROMO_PRICING':
      return { ...state, promoPricing: action.payload };

    case 'SET_MASTER_PROMO': {
      // Applies the auto-master promo (country default or SF-active code)
      // without touching the user-visible input. Also refreshes promoTerms
      // so the "See Terms and Conditions" modal reflects the applied
      // master. Falls back to the live-fetched or bundled FITTINGUS copy
      // when SF ships no terms alongside the code.
      //
      // ── Unless the visitor applied their own code ────────────────────
      // This used to overwrite promoTerms unconditionally, which is why an
      // applied referral code showed the master promotion's terms: the
      // visitor applies their code, VALIDATE_PROMO_SUCCESS swaps in the
      // right terms, and then any later SET_MASTER_PROMO — a store change,
      // a re-fetch of /fittings — silently puts the master's terms back.
      // Nothing surfaces, because both texts are real terms.
      //
      // A validated, non-default code owns the terms modal. Salesforce
      // returns each code's own terms (verified 2026-08-20: POLI113429
      // returns its own 908-char referral text, not the base promo's), so
      // whatever validation put there is already correct and the master
      // has no business replacing it.
      const validatedCode = state.promoValidatedCode?.trim().toUpperCase();
      const visitorPromoOwnsTerms =
        state.promoValidation?.valid === true &&
        !!validatedCode &&
        validatedCode !== DEFAULT_PROMO_CODE;

      return {
        ...state,
        masterPromoCode: action.payload.code,
        promoTerms: visitorPromoOwnsTerms
          ? state.promoTerms
          : action.payload.terms
            ? action.payload.terms
            : state.defaultPromoTermsLive ?? defaultPromoTermsForCountry(state.country),
      };
    }

    case 'SET_PROMO_CODE': {
      // Editing the code invalidates any prior validation result — the new
      // string hasn't been validated yet. Also clears promoValidatedCode
      // so the user can re-enter the same code after a Remove (otherwise
      // canValidate would see "code matches last validated" and gate the
      // Apply button).
      const codeChanged = action.payload !== state.promoCode;
      // Reset terms to DEFAULT_PROMO_TERMS whenever the user clears the
      // input or types FITTINGUS — those are the two states that mean "no
      // extra promo is in play, the default applies".
      const upper = action.payload.trim().toUpperCase();
      const isDefaultOrEmpty = upper === '' || upper === DEFAULT_PROMO_CODE;
      return {
        ...state,
        promoCode: action.payload,
        promoValidation: codeChanged ? null : state.promoValidation,
        promoValidationError: codeChanged ? null : state.promoValidationError,
        promoValidatedCode: codeChanged ? null : state.promoValidatedCode,
        promoTerms: isDefaultOrEmpty
          ? state.defaultPromoTermsLive ?? defaultPromoTermsForCountry(state.country)
          : state.promoTerms,
      };
    }

    case 'SET_DEFAULT_PROMO_TERMS_LIVE': {
      const live = action.payload?.trim();
      if (!live) return state;
      // Replace promoTerms in place ONLY when the current view is the
      // default copy. If a non-FITTINGUS validated promo has already
      // swapped its terms in, leave that alone.
      const promoTerms =
        state.promoTerms === DEFAULT_PROMO_TERMS ? live : state.promoTerms;
      return { ...state, defaultPromoTermsLive: live, promoTerms };
    }

    case 'VALIDATE_PROMO_START':
      return {
        ...state,
        promoValidating: true,
        promoValidationError: null,
      };

    case 'VALIDATE_PROMO_SUCCESS': {
      // Swap the active terms only on a successful validation of a
      // non-FITTINGUS code. Use the SF-returned `terms` when it carries
      // text, otherwise fall back to MOCK_VALIDATED_PROMO_TERMS so the
      // modal still has something to show. Invalid or default-token
      // results leave the existing terms (likely DEFAULT_PROMO_TERMS)
      // intact so View Details keeps surfacing the FITTINGUS terms.
      const codeUpper = action.payload.code.trim().toUpperCase();
      const isNonDefaultValid =
        action.payload.valid && codeUpper && codeUpper !== DEFAULT_PROMO_CODE;
      const nextTerms = isNonDefaultValid
        ? action.payload.terms?.trim()
          ? action.payload.terms
          : MOCK_VALIDATED_PROMO_TERMS
        : state.promoTerms;
      return {
        ...state,
        promoValidating: false,
        promoValidation: {
          valid: action.payload.valid,
          discount: action.payload.discount,
          message: action.payload.message,
        },
        promoValidatedCode: action.payload.code,
        promoValidationError: null,
        promoTerms: nextTerms,
        // Drop the previous code's prices. widget.tsx re-resolves them for the
        // new code, but only DISPATCHES on success — a resolution that fails,
        // or one still in flight, would otherwise leave the old code's prices
        // on the cards beside the new code's terms. CC reported exactly that
        // in UK testing and read it as browser caching; it is ours. Cleared,
        // the grid falls back to list prices until the new answer lands, which
        // is honest rather than stale.
        promoPricing: null,
      };
    }

    case 'VALIDATE_PROMO_ERROR':
      return {
        ...state,
        promoValidating: false,
        promoValidation: null,
        promoValidationError: action.payload,
      };

    case 'SUBMIT_START':
      return { ...state, submitting: true, submitError: null };

    case 'SUBMIT_SUCCESS':
      return { ...state, submitting: false, confirmation: action.payload };

    case 'SUBMIT_ERROR':
      return { ...state, submitting: false, submitError: action.payload };

    case 'CLEAR_SUBMIT_ERROR':
      return { ...state, submitError: null };

    case 'FLASH_REQUIRED_ERRORS':
      return { ...state, showRequiredErrors: true };

    case 'WAITLIST_SUBMIT_START':
      return { ...state, waitlistSubmitting: true, waitlistError: null };

    case 'WAITLIST_SUBMIT_SUCCESS':
      // Lands in the same `confirmation` field as a normal booking. The
      // status discriminator ('waitlisted' vs 'confirmed') drives downstream
      // UI copy (BookingConfirmed renders waitlist messaging when set).
      return {
        ...state,
        waitlistSubmitting: false,
        confirmation: action.payload,
      };

    case 'WAITLIST_SUBMIT_ERROR':
      return { ...state, waitlistSubmitting: false, waitlistError: action.payload };

    case 'SET_HEAR_ABOUT': {
      const next = { ...state, ...action.payload };
      // Choosing anything other than Other drops the free-text, so a stale
      // answer cannot ride along with a different choice.
      if (action.payload.hearAbout !== undefined && action.payload.hearAbout !== 'Other') {
        next.hearAboutAdditional = '';
      }
      return next;
    }

    case 'POST_BOOKING_FIELD':
      return { ...state, ...action.payload };

    case 'POST_BOOKING_SUBMIT_START':
      return { ...state, postBookingSubmitting: true, postBookingError: null };

    case 'POST_BOOKING_SUBMIT_SUCCESS':
      return {
        ...state,
        postBookingSubmitting: false,
        postBookingError: null,
        postBookingComplete: true,
      };

    case 'POST_BOOKING_SUBMIT_ERROR':
      return { ...state, postBookingSubmitting: false, postBookingError: action.payload };

    // ── Reschedule flow ─────────────────────────────────────────────────
    // ENTER is deliberately idempotent-and-clearing: re-entering (including
    // the picker's Back link) drops any prior lookup result so the user is
    // back at a clean name+email form, still in reschedule mode.
    case 'ENTER_RESCHEDULE_MODE':
      return {
        ...state,
        rescheduleMode: true,
        rescheduleCandidates: null,
        originalAppointment: null,
        rescheduleLookupSubmitting: false,
        rescheduleLookupError: null,
      };

    case 'EXIT_RESCHEDULE_MODE':
      return {
        ...state,
        rescheduleMode: false,
        rescheduleCandidates: null,
        originalAppointment: null,
        rescheduleLookupSubmitting: false,
        rescheduleLookupError: null,
      };

    case 'RESCHEDULE_LOOKUP_START':
      return { ...state, rescheduleLookupSubmitting: true, rescheduleLookupError: null };

    case 'RESCHEDULE_LOOKUP_SUCCESS':
      return {
        ...state,
        rescheduleLookupSubmitting: false,
        rescheduleLookupError: null,
        rescheduleCandidates: action.payload.candidates,
      };

    case 'RESCHEDULE_LOOKUP_ERROR':
      return {
        ...state,
        rescheduleLookupSubmitting: false,
        rescheduleLookupError: action.payload.message,
      };

    case 'HYDRATE_FROM_APPOINTMENT':
      // One atomic action, going around the SELECT_* cascade the same way
      // SWITCH_TO_SLOT_AT_LOCATION does — dispatching SELECT_LOCATION then
      // per-field selects would let the location cascade wipe the hydrated
      // fitting/date/slot. fittingNameMap is nulled so widget.tsx's
      // listStoreFittings effect (which fires because selectedLocation
      // changed) repopulates the store's wire-name map; promoPricing is
      // nulled for the same store-scoping reason SELECT_LOCATION nulls it.
      return {
        ...state,
        originalAppointment: action.payload.appointment,
        rescheduleCandidates: null,
        rescheduleLookupSubmitting: false,
        rescheduleLookupError: null,
        selectedLocation: action.payload.location,
        fittingNameMap: null,
        fittingListPrices: null,
        promoPricing: null,
        selectedFittingType: action.payload.fittingType,
        selectedDate: action.payload.date,
        selectedFitterId: null,
        selectedTime: action.payload.slot?.start ?? null,
        selectedSlot: action.payload.slot,
        phone: action.payload.phone,
        showRequiredErrors: false,
      };

    case 'RESET':
      // Preserve the SF-fetched FITTINGUS terms and the resolved master
      // promo across resets — both are session-stable and re-fetching
      // after a flow reset just adds latency + risks a brief window
      // where the wrong promo code is sent on Schedule.
      return {
        ...initialState,
        masterPromoCode: state.masterPromoCode,
        defaultPromoTermsLive: state.defaultPromoTermsLive,
        promoTerms: state.promoTerms,
      };

    case 'RESET_PRESERVE_LEAD':
      return {
        ...initialState,
        fullName: state.fullName,
        email: state.email,
        phone: state.phone,
        promoOptIn: state.promoOptIn,
        // Carried over deliberately: they have already agreed, and clearing it
        // would re-block a customer who is only starting a second booking.
        privacyAccepted: state.privacyAccepted,
        dialCodeOverride: state.dialCodeOverride,
        masterPromoCode: state.masterPromoCode,
        defaultPromoTermsLive: state.defaultPromoTermsLive,
        promoTerms: state.promoTerms,
      };
  }
}
